import type { FieldValues } from 'react-hook-form';

export const capitalizeWords = (words: string) =>
  words
    ?.trim()
    ?.split(' ')
    ?.map((word) => word[0]?.toUpperCase() + word.substr(1))
    ?.join(' ');

export const capitalizeVariables = (words: string) => {
  return words
    ?.trim()
    ?.split('_')
    ?.map((word) => word[0]?.toUpperCase() + word.substr(1))
    ?.join(' ')
    ?.split('-')
    ?.map((word) => word[0]?.toUpperCase() + word.substr(1))
    ?.join(' ');
};

// Capitalizes all words except the predetermined words to keep lowercase
// "ready for review" > "Ready for Review"
export const toTitleCase = (words: string) => {
  if (!words) return '';

  const lowercaseWords = new Set([
    'a',
    'an',
    'and',
    'as',
    'at',
    'but',
    'by',
    'for',
    'in',
    'nor',
    'of',
    'on',
    'or',
    'so',
    'the',
    'to',
    'up',
    'yet'
  ]);

  return words
    .trim()
    .toLowerCase()
    .split(/\s+/)
    .map((word, index) => {
      if (index === 0 || !lowercaseWords.has(word)) {
        return word.charAt(0).toUpperCase() + word.slice(1);
      } else {
        return word;
      }
    })
    .join(' ');
};

export const isUuid = (value: string) =>
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(value);

export const isNumber = (value: string | number) => {
  if (!value) {
    return false;
  }

  return !isNaN(+value);
};

/**
 * Returns number if the @value is a number or
 * a stringified version of a number.
 *
 * Usecase
 * - Make the values being passed to row of a
 * table sortable, especially when the string contains only number
 *
 * Returns the @value passed in all the other cases.
 * @param value
 * @returns sortable string or a number
 */
export const makeSortable = (value: string | number) => {
  if (!value) {
    return value;
  }

  // Value maybe number or a string maybe a number
  if (isNumber(value)) {
    return Number(value);
  } else {
    return value;
  }
};

export const str2bool = (value: string) => {
  if (value && typeof value === 'string') {
    if (value.toLowerCase() === 'true' || value === '1') {
      return true;
    }
    if (value.toLowerCase() === 'false' || value === '0') {
      return false;
    }
  }

  return value;
};

export const camelToTitleSpaced = (value: string) =>
  value?.replace(/([A-Z]+)/g, ' $1').replace(/([A-Z][a-z])/g, ' $1');

export const cleanString = function (input: string) {
  let output = '';
  for (let i = 0; i < input.length; i++) {
    if (input.charCodeAt(i) <= 127) {
      output += input.charAt(i);
    }
  }
  // While o isn't a special character we're cleaning up the rest of the bullets
  // and it looks weird to leave it in the narrative when it's on it's own
  // when it's wrapped with the new line and tab it should only be a bullet point
  output = output.replace(/\no\t/g, '\n\t');
  return output;
};

export const cleanFormStrings = (data: FieldValues): FieldValues => {
  // Clean up the data before sending it to the server
  const cleanedData: FieldValues = { ...data };
  // Trim all string values in cleanedData
  Object.keys(cleanedData).forEach((key) => {
    if (typeof cleanedData[key] === 'string') {
      cleanedData[key] = cleanedData[key].trim();
    }
  });

  return cleanedData;
};

export const removeUnderscore = (value: string) => value?.replace(/_/g, ' ');

export const escapeHtml = (s: string) =>
  s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
